import * as React from 'react';
import { Box, Stack, Typography } from '@mui/material';

export interface BreakdownItem {
  label: string;
  value: number | string;
  bold?: boolean;
}

export interface BreakdownGroup {
  heading?: string;
  items: BreakdownItem[];
}

interface BreakdownProps {
  /** Flat list of metrics rendered as a row of big-number/small-label pairs. */
  items?: BreakdownItem[];
  /** Grouped key:value lines, optionally headed. Use when there is sub-structure. */
  groups?: BreakdownGroup[];
}

const formatValue = (v: number | string) =>
  typeof v === 'number' ? new Intl.NumberFormat('en-US').format(v) : v;

/**
 * Breakdown — companion helper for `CommonCard`. Drop in as `children` to
 * render either:
 *   • a horizontal row of metrics (`items` prop), or
 *   • one or more grouped key:value lists (`groups` prop, with headings).
 */
const Breakdown: React.FC<BreakdownProps> = ({ items, groups }) => {
  if (groups && groups.length > 0) {
    return (
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
        {groups.map((group, idx) => (
          <Box key={group.heading ?? idx} sx={{ minWidth: 120 }}>
            {group.heading && (
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                {group.heading}
              </Typography>
            )}
            {group.items.map((item) => (
              <Typography
                key={item.label}
                sx={{
                  fontSize: 12,
                  fontWeight: item.bold ? 700 : 500,
                  color: 'rgba(15, 23, 42, 0.7)',
                  lineHeight: 1.7
                }}
              >
                {item.label}: {formatValue(item.value)}
              </Typography>
            ))}
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
      {(items ?? []).map((item) => (
        <Box key={item.label}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
            {formatValue(item.value)}
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 500, color: 'rgba(15, 23, 42, 0.55)', mt: 0.25 }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

export default Breakdown;
